import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { isEmpty, debounce } from "lodash";
import {
  chevronLeftIcon,
  menuDeleteIcon,
  menuDuplicateIcon,
  viewIcon,
  menuAltIcon,
} from "../../assets/icons";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import {
  EmptyLayout,
  Footer,
  LoadingPage,
  SearchBar,
  SuggestionBar,
} from "../../components/molecules";
import {
  deleteCollection,
  selectCollections,
  selectCollectionsFilters,
  selectSelectedDataModelId,
  selectSelectedDataModel,
  setCollection,
  CollectionCard,
  setShouldShowLoadingPage,
  selectShouldShowLoadingPage,
  selectDataModelWithPendingResources,
} from "./index";
import { actions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import {
  POPOVER_BUTTONS,
  FOOTER_CONSTANTS,
  STATUS,
  TABS,
  SEARCH_DEBOUNCE_TIME,
} from "../../constants";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import {
  selectAreResourcesPending,
  selectDataModelRegions,
} from "./dataModels.selectors";
import { getDataModelSuggestionMessage, getRegion } from "./services.helpers";
import { renderNoDataModelLayout } from "../../helpers";
import { fetchEndpoints } from "../../pages/DataModels";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as spinnerActions } from "../../components/organisms/Spinner/spinner.reducers";
const { VIEW, DELETE, DUPLICATE } = POPOVER_BUTTONS;
const { setSelectedDataModelId, setCollectionsFilters } = actions;
const { setSelectedDrawer } = drawerActions;
const { MANAGE_COLLECTION_DRAWER } = DRAWERS;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;
const { removeSpinner } = spinnerActions;
const { COLLECTIONS_TITLE, COLLECTIONS_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_MODAL, LANDING_MODAL, IMPORT_DATAMODAL_MODAL } = MODALS;
const { INACTIVE } = STATUS;
const { SETTINGS } = TABS;
const {
  DELETE_COLLECTION,
  FETCH_COLLECTIONS,
  LOADING_PAGE,
  FETCH_DATA_MODELS,
} = SPINNER_NAMES;
const NAVIGATE_BACK = -1;

const Collections = () => {
  const currentPath = window.location.pathname;
  const { dataModelId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: allCollections, total: totalCollections } =
    useSelector(selectCollections);
  const filters = useSelector(selectCollectionsFilters);
  const selectedDataModelId = useSelector(selectSelectedDataModelId);
  const shouldShowLoadingPage = useSelector(selectShouldShowLoadingPage);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const dataModelRegions = useSelector(selectDataModelRegions);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowSuggestionBar, setShouldShowSuggestionBar] = useState(false);
  const areResourcesPending = useSelector(selectAreResourcesPending);
  const dataModelWithPendingResources = useSelector(
    selectDataModelWithPendingResources
  );

  const getFormattedProperties = (collection) => {
    let properties = {};
    collection.schema.properties.forEach(
      (property) => (properties[property.schema.key] = property.schema.type)
    );
    return properties;
  };

  useEffect(() => {
    if (selectedDataModel) {
      const showSuggestionBar = Boolean(
        selectedDataModel.status === INACTIVE ||
          (dataModelRegions &&
            selectedDataModel.region !== getRegion(dataModelRegions))
      );
      setShouldShowSuggestionBar(showSuggestionBar);
      const checkShouldShowLandingModal =
        selectedDataModel.trackDatabaseSetup.some(
          ({ isSetupCompleted }) => !isSetupCompleted
        );
      const loadPage = setTimeout(() => {
        if (shouldShowLoadingPage) {
          dispatch(setShouldShowLoadingPage(false));
          dispatch(removeSpinner(LOADING_PAGE));
          selectedDataModel.showSetupModal &&
            checkShouldShowLandingModal &&
            dispatch(setSelectedModal(LANDING_MODAL));
        }
      }, 1000);

      return () => clearTimeout(loadPage);
    }
  }, [dispatch, selectedDataModel, shouldShowLoadingPage, dataModelRegions]);

  useEffect(() => {
    if (
      areResourcesPending &&
      selectedDataModel?.id === dataModelWithPendingResources?.id
    )
      dispatch(setSelectedModal(IMPORT_DATAMODAL_MODAL));

    return () => dispatch(setSelectedModal());
  }, [
    areResourcesPending,
    dispatch,
    selectedDataModel,
    dataModelWithPendingResources,
  ]);

  useEffect(() => {
    if (!selectedDataModelId && dataModelId)
      dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId, selectedDataModelId]);

  useEffect(() => {
    return () => {
      dispatch(setCollectionsFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setCollection(null));
  }, [dispatch, currentPath]);

  const popOverOption = (row) => [
    {
      name: VIEW,
      icon: viewIcon,
      handleOnClick: () => {
        dispatch(setCollection(row));
        navigate(`/data-models/${dataModelId}/collections/${row.id}/details`);
      },
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: () => console.log("Clicked duplicate"),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setItemToBeDeleted(row));
        dispatch(setSelectedModal(DELETE_MODAL));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  useEffect(() => {
    if (selectedDataModel) dispatch(setSelectedTab(selectedDataModel));
  }, [dispatch, dataModelId, filters, selectedDataModel]);

  const handleDeleteCollection = useCallback(
    (row) => {
      dispatch(deleteCollection(row.id)).then((res) => {
        if (res.payload) {
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          setShouldShowDeleteModal(false);
          dispatch(
            fetchEndpoints({
              id: selectedDataModelId,
            })
          );
        }
      });
    },
    [dispatch, selectedDataModelId]
  );

  const handleOnSearch = debounce(
    (name) => dispatch(setCollectionsFilters({ name })),
    SEARCH_DEBOUNCE_TIME
  );

  const renderCollectionsLayout = () =>
    !isEmpty(allCollections) ? (
      <>
        <div className="grid grid-cols-12 gap-4">
          {allCollections.map((collection) => {
            const schema = {
              id: typeof collection.id,
              name: typeof collection.name,
              properties: getFormattedProperties(collection),
            };
            return (
              <CollectionCard
                schema={schema}
                name={collection.name}
                popOverOptions={popOverOption(collection)}
                key={collection.id}
              />
            );
          })}
          {shouldShowDeleteModal && (
            <DeleteConfirmationModal
              handleDelete={handleDeleteCollection}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              title="Delete Collection"
              spinner={DELETE_COLLECTION}
            />
          )}
        </div>
      </>
    ) : (
      <EmptyLayout
        title="No Collections"
        subTitle="You can get started by creating a new collection."
        buttonText="Create New Collection"
        image={menuAltIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedDrawer(MANAGE_COLLECTION_DRAWER)),
    [dispatch]
  );

  useEffect(() => () => dispatch(setShouldShowLoadingPage(false)), [dispatch]);

  return (
    <>
      <Helmet>
        <title>Collections | TavaJS</title>
      </Helmet>
      {shouldShowLoadingPage ? (
        <LoadingPage
          title={"Setting Up Your Database"}
          subtitle={"Check Back in Couple minutes"}
        />
      ) : (
        <div className="content scrollbar overflow-auto p-6 flex flex-col flex-1">
          <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4">
            <div className="flex">
              <button
                className="bg-gray-800 h-10 px-2 rounded-md hover:bg-gray-700/60 mr-1"
                onClick={() => navigate(NAVIGATE_BACK)}
              >
                <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
              </button>
              {selectedDataModel && !!totalCollections && (
                <SearchBar
                  placeholder="Search"
                  handleOnSearch={handleOnSearch}
                />
              )}
            </div>
            {selectedDataModel && (
              <button
                type="button"
                className="btn btn-primary w-full xs:w-auto"
                onClick={() => handleShouldShowDrawer()}
              >
                Create New Collection
              </button>
            )}
          </div>
          {selectedDataModel && shouldShowSuggestionBar && (
            <SuggestionBar
              text={getDataModelSuggestionMessage(
                selectedDataModel,
                dataModelRegions
              )}
              link={SETTINGS}
              handleOnClick={() =>
                navigate(`/data-models/${dataModelId}/settings`)
              }
              classes="text-red-600"
            />
          )}
          <div className="justify-between flex flex-col flex-1">
            <Spinner name={[FETCH_COLLECTIONS, FETCH_DATA_MODELS]}>
              {selectedDataModel || totalCollections || selectedDataModelId
                ? renderCollectionsLayout()
                : renderNoDataModelLayout(navigate)}
            </Spinner>
          </div>
          {selectedDataModel && (
            <Footer title={COLLECTIONS_TITLE} subtitle={COLLECTIONS_SUBTITLE} />
          )}
        </div>
      )}
    </>
  );
};

export default Collections;
